import type { Directive, DirectiveBinding, VNode } from 'vue'
import { useOneTooltip } from './useOneTooltip'

type Value = string | null | { content: string | VNode | null }
type Placement = 'top' | 'right' | 'bottom' | 'left'

const { showTooltip, hideTooltip } = useOneTooltip()

// We use a WeakMap here to store each element's events and content
// to ensure that the tooltip data is garbage collected
// when the trigger element is no longer in use.
const tooltipData = new WeakMap<
	HTMLElement,
	{
		onMouseEnter: EventListener
		onMouseLeave: EventListener
		content: string | VNode | null
		observer?: MutationObserver
	}
>()

const createContent = (value: Value) => {
	if (typeof value === 'string') return value
	return value?.content ?? null
}

const bind = (el: HTMLElement, binding: DirectiveBinding<Value, Placement>) => {
	const { value, modifiers } = binding
	const placement = (Object.keys(modifiers)[0] || 'top') as Placement

	const onMouseEnter = () => {
		const data = tooltipData.get(el)

		// If the content is null, we don't want to show the tooltip
		if (data?.content === null) return

		showTooltip({ trigger: el, content: data?.content, placement })
	}

	const onMouseLeave = () => {
		hideTooltip()
	}

	const observer = new MutationObserver(() => {
		if (!document.body.contains(el)) {
			hideTooltip()
			observer.disconnect()
		}
	})
	observer.observe(document.body, { childList: true, subtree: true })

	// Initialize the tooltip data for this element
	tooltipData.set(el, {
		onMouseEnter,
		onMouseLeave,
		content: createContent(value),
		observer,
	})

	el.addEventListener('mouseenter', onMouseEnter)
	el.addEventListener('mouseleave', onMouseLeave)

	// Handle conditional rendering
	new MutationObserver((mut) => {
		for (const a of mut) {
			for (const b of a.removedNodes) {
				if (b === el) {
					hideTooltip()
					return
				}
			}
		}
	}).observe(el.parentElement!, { childList: true })
}

const destroy = (el: HTMLElement) => {
	const data = tooltipData.get(el)

	if (data) {
		el.removeEventListener('mouseenter', data.onMouseEnter)
		el.removeEventListener('mouseleave', data.onMouseLeave)
		data.observer?.disconnect()
		tooltipData.delete(el)
	}
	hideTooltip()
}

const vOneTooltip: Directive<HTMLElement> = {
	mounted: bind,
	beforeUnmount(el: HTMLElement) {
		destroy(el)
	},
	updated(el: HTMLElement, binding: DirectiveBinding<Value, Placement>) {
		const data = tooltipData.get(el)
		const { value, oldValue } = binding

		// Only update the content if the value has changed
		if (data && value !== oldValue) {
			data.content = createContent(value)
			if (data.content === null) hideTooltip()
		}
	},
}

export { vOneTooltip }
